import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getRightsholders } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useCreateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

export function CreateRightsholderModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const rightsholders = useSelector(getRightsholders)
  const {
    searchText = '',
    splitId,
    splitsKey,
  } = (location.state as RightsDataModalLocationState) || {}

  const createEntity = useCreateRightsEntity({
    entityType: 'Label',
  })

  const [inputValue, setInputValue] = useState(searchText)

  const existingProNames = useMemo(
    () => rightsholders.map(p => p.name),
    [rightsholders],
  )

  const duplicateProName = useMemo(
    () => findDuplicateName(inputValue, existingProNames),
    [inputValue, existingProNames],
  )

  const warningMessage = duplicateProName
    ? `${duplicateProName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      const createdRightsholderId = await createEntity(
        name,
        externalIdentifiers,
      )

      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { rightsholderId: createdRightsholderId },
          }),
        )
      }
    },
    [dispatch, splitId, splitsKey, createEntity],
  )

  return (
    <BaseDialog
      heading="Create new rightsholder"
      nameFieldLabel="Rightsholder name"
      onSave={handleSave}
      initialName={searchText}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showISNI
      showIPI
      open
    />
  )
}

export default CreateRightsholderModal
